import React from 'react';
import {ButtonBase} from '../elements';
import {useDispatch, useSelector} from "react-redux";
import {
    ADD_COUPON_EVENT,
    CHANGE_NEED_CONFIRM_DIALOG_SETTINGS,
    getMarketLimitAsync,
    NEED_CONFIRM_COUPON_EVENT,
    REMOVE_COUPON_EVENT,
    UPDATE_COUPON_MESSAGE
} from '../../store/spirs/actions';
import {StoreType} from "../../store/spirs/types";
import {FixtureElement, MarketElement, SelectionElement} from "../../models/api/codegen/code";
import {CouponEvent} from "../../store/spirs/reducers/couponReducer";
import {useRate} from "./DefaultRate";
import {LockIcon} from "../CommonSportDisplay/desktop/Sport/imgs/LockIcon";
import {useTranslation} from "react-i18next";
import {Visible} from "../../utils/Visible";
import {sendFingerprint} from "../../utils/SendFingerprint";
import iziToast from "izitoast";
import clsx from "clsx";
import {Desktop} from "../adaptive/Desktop";
import {Mobile} from "../adaptive/Mobile";
import {showCustomToast} from '../../utils/Toasts';

type CoefficientBox = {
    selectionEl: SelectionElement;
    marketName: MarketElement['n'];
    marketId: MarketElement['i'];
    fixture: FixtureElement;
    isActive: boolean;
    minRate?: number
    maxRate?: number
    isPlaceholder: boolean
    hasOverlay: boolean
};

export const CoefficientBox = React.memo((props: CoefficientBox) => {
    const { t, i18n: { language } } = useTranslation();

    const { selectionEl, fixture, marketName, marketId, isActive, minRate, maxRate, isPlaceholder, hasOverlay } = props;
    const dispatch = useDispatch();
    const currentCouponType = useSelector((state: StoreType) => state.spirs.coupons.currentCouponType);
    const currentCouponEvents = useSelector((state: StoreType) => state.spirs.coupons[currentCouponType]);
    const needConfirm = useSelector((state: StoreType) => state.spirs.playerSettings.needConfirm);
    const rate = useRate(fixture.s);
    const playerToken = useSelector((state: StoreType) => state.spirs.playerToken);
    const playerIsFrozen = useSelector((state: StoreType) => state.spirs.playerInfo.frozen);
    const frozenUpTo = useSelector((state: StoreType) => state.spirs.playerInfo.frozenUpTo);

    const selected = useSelector((state: StoreType) =>
        !!state.spirs.coupons[state.spirs.coupons.currentCouponType]?.find((event: CouponEvent) => event.selectionId === selectionEl.i)
    );

    const addEventToCoupon = React.useCallback(
        () => {
            if (isPlaceholder || hasOverlay) {
                return;
            }
            if (fixture.cl && selectionEl.al) {
                sendFingerprint();
                return window.location.replace(selectionEl.al + '/' + language);
            }
            if (playerToken === 'GUEST' && currentCouponEvents.length >= 10) {
                showCustomToast(t('coupons.guestLimitReached'))
                return;
            }
            if (playerIsFrozen) {
                iziToast.info({
                    id: 'my-toast',
                    backgroundColor:'black',
                    messageColor:'white',
                    maxWidth: 300,
                    message: t("coupons.frozenAccError", {to: frozenUpTo ? frozenUpTo : t("coupons.freezeType.INDEFINITELY")}),
                    position: "center",
                    onOpening: () => {
                        const toast = document.querySelector('.iziToast') as HTMLElement;
                        toast.style.position = "fixed";
                        toast.style.top = "50%";
                        toast.style.left = "50%";
                        toast.style.transform = "translate(-50%, -50%)";
                    },
                    progressBar: false,
                    messageSize:'14px',
                    closeOnClick: true,
                });
                return;
            }
            if ((currentCouponType === 'EXPRESS' || currentCouponType === 'SYSTEM')
                && currentCouponEvents.find((el: CouponEvent) => el.fixtureId === fixture.fi)) {
                dispatch(UPDATE_COUPON_MESSAGE(t('coupons.alreadyExistFixtureMessage')));
            } else {
                dispatch(UPDATE_COUPON_MESSAGE(''));
                const couponEvent: CouponEvent = {
                    fixtureId: fixture.fi,
                    selectionName: selectionEl.n,
                    competitionName: fixture.cn,
                    selectionId: selectionEl.i,
                    rate,
                    active: true,
                    odds: selectionEl.o,
                    marketName,
                    marketId,
                    minRate,
                    maxRate,
                    live: fixture.s === 'LIVE'
                };
                if (needConfirm) {
                    dispatch(NEED_CONFIRM_COUPON_EVENT(couponEvent));
                    dispatch(CHANGE_NEED_CONFIRM_DIALOG_SETTINGS('event-selection-id-' + selectionEl.i));
                } else {
                    dispatch(ADD_COUPON_EVENT(couponEvent));
                }
                dispatch(getMarketLimitAsync.request({
                    marketId: couponEvent.marketId,
                    fixtureId: couponEvent.fixtureId
                }));
            }
            if (currentCouponEvents.find((el: CouponEvent) => el.selectionId === selectionEl.i)) {
                dispatch(REMOVE_COUPON_EVENT(selectionEl.i));
            }

        }, [currentCouponType, currentCouponEvents, rate, hasOverlay]);

    const onClickMobile = () => {
        return iziToast.info({
            message: `
                    <div class="flex align-senter px-[20px] py-[15px] rounded-[4px]">
                        <div class="flex-shrink-0 mr-[15px]"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 0C18.6281 0 24 5.37187 24 12C24 18.6281 18.6281 24 12 24C5.37187 24 0 18.6281 0 12C0 5.37187 5.37187 0 12 0ZM9.67969 7.48828C9.21328 7.48828 8.83594 7.86562 8.83594 8.33203C8.83594 8.79844 9.21328 9.17578 9.67969 9.17578H11.0859V16.5371H9.08887C8.62268 16.5372 8.24533 16.9147 8.24512 17.3809C8.24512 17.8472 8.62255 18.2245 9.08887 18.2246H14.7705C15.2368 18.2245 15.6143 17.8472 15.6143 17.3809C15.614 16.9147 15.2367 16.5372 14.7705 16.5371H12.7734V8.33203C12.7734 7.86562 12.3961 7.48828 11.9297 7.48828H9.67969ZM11.9297 4.32422C11.4633 4.32422 11.0859 4.70156 11.0859 5.16797C11.0859 5.63438 11.4633 6.01172 11.9297 6.01172C12.3961 6.01172 12.7734 5.63438 12.7734 5.16797C12.7734 4.70156 12.3961 4.32422 11.9297 4.32422Z" fill="white" fill-opacity="0.65"/></svg>
                        </div>
                        <div class="text-[14px] font-normal">${t('chessLive.mobile_error')}</div>
                    </div>
            `,
            position: 'center',
            backgroundColor: '#383E45',
            messageColor: 'rgba(255, 255, 255, 0.85)',
            messageSize: '14px',
            maxWidth: 414,
            progressBar: false,
            timeout: 3000,
            onOpening: () => {
                const toast = document.querySelector('.iziToast') as HTMLElement;
                const toastBody = document.querySelector('.iziToast-body') as HTMLElement;
                const toastClose = document.querySelector('.iziToast-close') as HTMLElement;
                toastClose.style.display = "none";
                toastBody.style.padding = '0';
                toast.style.zIndex = "10000";
                toast.style.position = "fixed";
                toast.style.top = "100px";
                toast.style.left = "50%";
                toast.style.transform = "translate(-50%, 0)";
            },
            closeOnClick: true
        });
    };

    const addEventToCouponMobile = React.useCallback(
        () => {
            if (isPlaceholder) {
                return;
            }
            if (fixture.cl && selectionEl.al) {
                return onClickMobile();
            }
            if (playerToken === 'GUEST' && currentCouponEvents.length >= 10) {
                showCustomToast(t('coupons.guestLimitReached'))
                return;
            }
            if ((currentCouponType === 'EXPRESS' || currentCouponType === 'SYSTEM')
                && currentCouponEvents.find((el: CouponEvent) => el.fixtureId === fixture.fi)) {
                dispatch(UPDATE_COUPON_MESSAGE(t('coupons.alreadyExistFixtureMessage')));
            } else {
                dispatch(UPDATE_COUPON_MESSAGE(''));
                const couponEvent: CouponEvent = {
                    fixtureId: fixture.fi,
                    selectionName: selectionEl.n,
                    competitionName: fixture.cn,
                    selectionId: selectionEl.i,
                    rate,
                    active: true,
                    odds: selectionEl.o,
                    marketName,
                    marketId,
                    minRate,
                    maxRate,
                    live: fixture.s === 'LIVE'
                };
                if (needConfirm) {
                    dispatch(NEED_CONFIRM_COUPON_EVENT(couponEvent));
                    dispatch(CHANGE_NEED_CONFIRM_DIALOG_SETTINGS('event-selection-id-' + selectionEl.i));
                } else {
                    dispatch(ADD_COUPON_EVENT(couponEvent));
                }
                dispatch(getMarketLimitAsync.request({
                    marketId: couponEvent.marketId,
                    fixtureId: couponEvent.fixtureId
                }));
            }
            if (currentCouponEvents.find((el: CouponEvent) => el.selectionId === selectionEl.i)) {
                dispatch(REMOVE_COUPON_EVENT(selectionEl.i));
            }

        }, [currentCouponType, currentCouponEvents, rate, isPlaceholder]);

    return (
        <Odds id={'event-selection-id-' + selectionEl.i}
              isActive={isActive}
              name={selectionEl.n}
              addEventToCoupon={addEventToCoupon}
              addEventToCouponMobile={addEventToCouponMobile}
              odds={selectionEl.o}
              selected={selected}
              isChess={fixture.cl}
              isPlaceholder={isPlaceholder}
        />
    );
});

const Odds: React.FC<{
    id: string,
    addEventToCoupon: () => void,
    addEventToCouponMobile: () => void,
    isActive: boolean,
    name: string,
    odds: number;
    selected: boolean,
    isChess: boolean,
    isPlaceholder: boolean
}> = React.memo((props) => {
    const { id, addEventToCoupon, addEventToCouponMobile, isActive, name, selected, isChess, isPlaceholder } = props;
    const [color, setColor] = React.useState('#EACD40');
    const [odds, setOdds] = React.useState(props.odds);
    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

    React.useEffect(() => {
        setOdds(props.odds);
        if (!isActive) {
            setColor('#a5a4a4');
            return;
        }
        if (props.odds > odds) {
            setColor('#2FF36B');
        } else if (props.odds < odds) {
            setColor('#FF2D2D');
        } else {
            setColor('#EACD40');
        }
        sleep(2000).then(() => setColor('#EACD40'));
    }, [props.odds, isActive]);


    return (
        <>
            <Desktop>
                <ButtonBase
                    id={id}
                    className={clsx(
                        "flex justify-between basis-0 flex-grow-[1] bg-[#1B1E23] py-[10px] px-[8px] rounded-[16px] mb-[8px] w-full",
                        "border border-transparent enabled:hover:border-[yellow] disabled:cursor-not-allowed",
                        "max-tablet:flex-col max-tablet:text-[10px]",
                        selected && "border !border-[#EACD40]"
                    )}
                    onClick={addEventToCoupon} disabled={!isActive}>
                    <span className="text-white">{name}</span>
                    <Visible value={!isChess}>
                        {!isPlaceholder &&
                            <span style={{color}} className={`max-tablet:mt-[5px] flex items-center gap-[5px]`}>{formatOdds(odds)} {!isActive &&
                                <LockIcon />}
                    </span>
                        }
                    </Visible>
                </ButtonBase>
            </Desktop>

            <Mobile>
                <ButtonBase
                    id={id}
                    className={clsx(
                        "flex items-center basis-0 flex-grow-[1] justify-center bg-[#1B1E23] py-[10px] px-[8px] rounded-[8px] mb-[8px] w-full",
                        "border border-transparent enabled:hover:any-hover:border-[yellow]",
                        "max-tablet:flex-col max-tablet:text-[10px]",
                        selected && "!border-[#EACD40]"
                    )}
                    onClick={addEventToCouponMobile} disabled={!isActive}>
                    <span className="text-white">{name}</span>
                    <Visible value={!isChess}>
                        {!isPlaceholder &&
                            <span className={`max-mobile:mt-[5px] text-[${color}] flex items-center gap-[5px]`}>{formatOdds(odds)} {!isActive &&
                                <LockIcon />}
                    </span>
                        }
                    </Visible>
                </ButtonBase>
            </Mobile>
        </>
    );
});

const formatOdds = (odds: number) => {
    function f(odds: number) {
        if (odds.toString().includes('.')) {
            const l = odds.toString().split('.').pop();
            return l ? l.length : 0;
        }
        return 0;
    }

    return f(odds) <= 2 ? odds : Math.floor(odds * 100) / 100;
};
